import { Select, TextInput } from '@mantine/core';
import { GridApi } from 'ag-grid-community';
import { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import useFilterValue from 'components/tables/filters/hooks/use-filter-value';
import { noop } from 'lodash';
import { forwardRef, useImperativeHandle } from 'react';

interface HrFloatingFilterProps {
  api: GridApi;
  onChange?: FilterChangeCallback;
}

interface HrFloatingFilterHandle {
  onParentModelChanged: () => void;
}

interface HrTextFilterConfig {
  filterKey: string;
  placeholder?: string;
  type?: 'equals' | 'contains';
}

interface HrSelectFilterConfig {
  filterKey: string;
  placeholder?: string;
  data: Array<{ value: string; label: string }>;
}

/** Compact text floating filter used by HR data-table columns. */
export const HrTextFloatingFilter = {
  configure: ({ filterKey, placeholder = 'Filtrovať', type = 'contains' }: HrTextFilterConfig) =>
    forwardRef<HrFloatingFilterHandle, HrFloatingFilterProps>(function ConfiguredHrTextFloatingFilter(
      { api, onChange },
      ref
    ) {
      useImperativeHandle(ref, () => ({ onParentModelChanged: noop }));
      const [value, setValue] = useFilterValue({ api, filterKey, onChange, type });

      return (
        <TextInput
          size="sm"
          w="100%"
          aria-label={placeholder}
          placeholder={placeholder}
          value={value ?? ''}
          onChange={(event) => setValue(event.currentTarget.value || null)}
        />
      );
    }),
};

/** Compact select floating filter used for finite HR table values. */
export const HrSelectFloatingFilter = {
  configure: ({ filterKey, placeholder = 'Všetky', data }: HrSelectFilterConfig) =>
    forwardRef<HrFloatingFilterHandle, HrFloatingFilterProps>(function ConfiguredHrSelectFloatingFilter(
      { api, onChange },
      ref
    ) {
      useImperativeHandle(ref, () => ({ onParentModelChanged: noop }));
      const [value, setValue] = useFilterValue({ api, filterKey, onChange, type: 'equals' });

      return (
        <Select
          clearable
          size="sm"
          w="100%"
          aria-label={placeholder}
          placeholder={placeholder}
          data={data}
          value={value}
          onChange={setValue}
        />
      );
    }),
};
